import {
  DefaultError,
  MutationCache,
  QueryCache,
  QueryClient,
  UseMutationOptions
} from '@tanstack/react-query'
import { HttpStatusCode, isAxiosError } from 'axios'
import i18next from 'i18next'
import { ApiError } from './ApiError'

export const BACK_OFFICE_PREFIX = import.meta.env.VITE_API_BACK_OFFICE_PREFIX
export const SNA_PREFIX = import.meta.env.VITE_API_SNA_PREFIX
export const SNA_IMAGE_PREFIX = import.meta.env.VITE_API_SNA_IMAGE_PREFIX

const RETRY_COUNT = 3
const JWT_REFRESH_URL = '/api/v1/auth/refresh'

export function createQueryClient(): QueryClient {
  const queryCache = new QueryCache()

  const mutationCache = new MutationCache()

  return new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: getShouldRetry
      }
    }
  })
}

function getShouldRetry(count: number, error: unknown) {
  if (count >= RETRY_COUNT) return false

  const axiosError =
    error instanceof ApiError && isAxiosError(error.source)
      ? error.source
      : null

  if (axiosError) {
    const status = axiosError.response?.status
    const url = axiosError.config?.url

    switch (status) {
      case HttpStatusCode.Unauthorized:
        // retry everything except token refresh
        return url != JWT_REFRESH_URL

      case HttpStatusCode.Forbidden:
        return false

      default:
        return true
    }
  }

  return false
}

export const queryClient = createQueryClient()

// Server data carries localized fields (modeltranslation `name`) resolved from
// the Accept-Language header. When the UI language changes, refetch everything
// so those fields come back in the newly selected language.
i18next.on('languageChanged', () => {
  queryClient.invalidateQueries()
})

/**
 * Helper type to make it easier to pass callbacks to mutation hooks by
 * 1. Reordering generic arguments via putting TVariable and TData types first as they're used more often
 *    this is also more natural to have args type first and then return value similary how we define function type.
 * 2. Allowing only necessary overrides, in this case callbacks (this can and should be extended as needed).
 */
export type MutationOptions<
  TVariables = void,
  TData = unknown,
  TContext = unknown,
  TError = DefaultError
> = Pick<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  'onSuccess' | 'onError' | 'onSettled' | 'onMutate'
>
